import { useCallback, useEffect, useRef, useState } from 'react';

const WIDTH_RATIO = 0.95;
const MIN_FONT_SIZE = 24;
const MAX_FONT_SIZE = 140;

function measureWidestTitle(probe, titles, fontSizePx) {
  probe.style.fontSize = `${fontSizePx}px`;
  let widest = 0;

  for (const title of titles) {
    probe.textContent = title;
    widest = Math.max(widest, probe.offsetWidth);
  }

  return widest;
}

function findFitFontSize(probe, titles, targetWidth) {
  let low = MIN_FONT_SIZE;
  let high = MAX_FONT_SIZE;
  let best = MIN_FONT_SIZE;

  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    const widest = measureWidestTitle(probe, titles, mid);

    if (widest <= targetWidth) {
      best = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }

  return best;
}

export function useSkillGlassTitleSize(titles, enabled) {
  const containerRef = useRef(null);
  const probeRef = useRef(null);
  const [fontSize, setFontSize] = useState(48);

  const measure = useCallback(() => {
    if (!enabled || !containerRef.current || titles.length === 0) {
      return;
    }

    if (!probeRef.current) {
      const probe = document.createElement('span');
      probe.style.cssText =
        'position:absolute;visibility:hidden;white-space:nowrap;pointer-events:none;top:-9999px;left:-9999px;font-weight:700;letter-spacing:-0.03em;line-height:0.88;font-family:"articulat-cf",sans-serif';
      document.body.appendChild(probe);
      probeRef.current = probe;
    }

    const targetWidth = containerRef.current.offsetWidth * WIDTH_RATIO;
    const nextSize = findFitFontSize(probeRef.current, titles, targetWidth);
    setFontSize(nextSize);
  }, [enabled, titles]);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    measure();

    const resizeObserver = new ResizeObserver(measure);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    window.addEventListener('resize', measure);

    let cancelled = false;
    const fontsReady = document.fonts
      ? Promise.all([
          document.fonts.ready,
          document.fonts.load('700 16px "articulat-cf"').catch(() => undefined),
        ])
      : Promise.resolve();

    fontsReady.then(() => {
      if (!cancelled) {
        measure();
      }
    });

    return () => {
      cancelled = true;
      resizeObserver.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [enabled, measure]);

  useEffect(() => {
    return () => {
      if (probeRef.current) {
        probeRef.current.remove();
        probeRef.current = null;
      }
    };
  }, []);

  return { containerRef, fontSize };
}
